import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchNotifications,
  markAllNotificationsRead,
  notificationQueryKeys,
} from "@/features/notifications/queries";
import { useAuth } from "@/features/auth/hooks/useAuth";

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const { data: notifications, isLoading, error } = useQuery({
    queryKey: notificationQueryKeys.list(user?.id ?? ""),
    queryFn: () => fetchNotifications(user!.id),
    enabled: !!user,
    staleTime: 1000 * 30,
  });

  // Realtime subscription for new notifications
  useEffect(() => {
    if (!user) return;

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.list(user.id),
          });
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(user!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.list(user!.id),
      });
    },
  });

  const unreadCount = (notifications ?? []).filter((n) => !n.read).length;

  return {
    notifications: notifications ?? [],
    unreadCount,
    isLoading,
    error: error as Error | null,
    markAllRead: markAllReadMutation.mutate,
  };
}
